import React, { useRef, useState, useEffect, useCallback } from 'react'
import {
  MessageCircle,
  Send,
  Globe,
  Instagram,
  Facebook,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

export interface ChannelFilterOption {
  readonly id: string
  readonly label: string
  readonly channelType: string
  readonly Icon: React.ComponentType<{ className?: string }>
  readonly iconColor: string
  readonly activeClass: string
  readonly unreadKey?: 'whatsapp' | 'telegram' | 'webchat' | 'instagram' | 'messenger'
}

export const CHANNEL_FILTER_OPTIONS: readonly ChannelFilterOption[] = [
  {
    id: 'all',
    label: 'Todos',
    channelType: 'all',
    Icon: MessageCircle,
    iconColor: 'text-slate-600',
    activeClass: 'bg-[#075f55] text-white border-[#075f55] shadow-xs',
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    channelType: 'WHATSAPP',
    Icon: MessageCircle,
    iconColor: 'text-[#25D366]',
    activeClass: 'bg-emerald-50 text-emerald-800 border-emerald-300 shadow-xs font-bold',
    unreadKey: 'whatsapp',
  },
  {
    id: 'telegram',
    label: 'Telegram',
    channelType: 'TELEGRAM',
    Icon: Send,
    iconColor: 'text-[#0088cc]',
    activeClass: 'bg-sky-50 text-sky-800 border-sky-300 shadow-xs font-bold',
    unreadKey: 'telegram',
  },
  {
    id: 'webchat',
    label: 'WebChat',
    channelType: 'WEBCHAT',
    Icon: Globe,
    iconColor: 'text-purple-600',
    activeClass: 'bg-purple-50 text-purple-800 border-purple-300 shadow-xs font-bold',
    unreadKey: 'webchat',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    channelType: 'INSTAGRAM',
    Icon: Instagram,
    iconColor: 'text-pink-600',
    activeClass: 'bg-pink-50 text-pink-800 border-pink-300 shadow-xs font-bold',
    unreadKey: 'instagram',
  },
  {
    id: 'messenger',
    label: 'Facebook',
    channelType: 'MESSENGER',
    Icon: Facebook,
    iconColor: 'text-blue-600',
    activeClass: 'bg-blue-50 text-blue-800 border-blue-300 shadow-xs font-bold',
    unreadKey: 'messenger',
  },
]

export interface ChannelFilterBarProps {
  selectedChannelType?: string
  onSelectChannel: (channelType: string) => void
  unreadCounts?: {
    whatsapp?: number
    telegram?: number
    webchat?: number
    instagram?: number
    messenger?: number
  }
  totalCounts?: {
    all?: number
    whatsapp?: number
    telegram?: number
    webchat?: number
    instagram?: number
    messenger?: number
  }
}

export function ChannelFilterBar({
  selectedChannelType,
  onSelectChannel,
  unreadCounts = {},
  totalCounts = {},
}: ChannelFilterBarProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    checkScroll()
    const el = scrollRef.current
    if (!el) return

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => checkScroll())
      resizeObserver.observe(el)
      return () => resizeObserver.disconnect()
    }
  }, [checkScroll])

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      scrollRef.current.scrollLeft += e.deltaY
      checkScroll()
    }
  }

  const scrollByAmount = (offset: number) => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({ left: offset, behavior: 'smooth' })
    setTimeout(checkScroll, 250)
  }

  const isChannelActive = (type: string) => {
    if (type === 'all') {
      return !selectedChannelType || selectedChannelType === 'all'
    }
    if (type === 'WHATSAPP') {
      return selectedChannelType === 'WHATSAPP' || selectedChannelType === 'WHATSAPP_CLOUD'
    }
    return selectedChannelType === type
  }

  return (
    <div className="relative group px-3 py-2 flex-shrink-0 bg-white border-b border-slate-100">
      {/* Botón de desplazamiento hacia la izquierda */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollByAmount(-140)}
          aria-label="Ver canales anteriores"
          className="absolute left-1.5 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full bg-white/95 border border-slate-200 text-slate-700 shadow-xs flex items-center justify-center hover:bg-slate-50 hover:scale-105 active:scale-95 transition-all focus:outline-none"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      {/* Difuminado suave a la izquierda */}
      {canScrollLeft && (
        <div className="absolute left-3 top-0 bottom-0 w-6 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none z-5" />
      )}

      {/* Contenedor desplazable de chips */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        onWheel={handleWheel}
        className="flex items-center gap-1.5 overflow-x-auto scroll-smooth py-0.5 px-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {CHANNEL_FILTER_OPTIONS.map((channel) => {
          const isActive = isChannelActive(channel.channelType)
          const ChannelIcon = channel.Icon
          const unread = channel.unreadKey ? unreadCounts[channel.unreadKey] ?? 0 : 0
          const total = channel.channelType === 'all'
            ? totalCounts?.all
            : channel.unreadKey
            ? totalCounts?.[channel.unreadKey]
            : undefined

          return (
            <button
              key={channel.id}
              type="button"
              onClick={() => onSelectChannel(channel.channelType)}
              className={`flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-xs font-semibold transition-all select-none border cursor-pointer ${
                isActive
                  ? channel.activeClass
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
              }`}
            >
              <ChannelIcon
                className={`h-4 w-4 ${isActive && channel.channelType === 'all' ? 'text-white' : channel.iconColor}`}
              />
              <span className="whitespace-nowrap">{channel.label}</span>
              {total !== undefined && total > 0 && (
                <span
                  className={`text-[10px] font-medium transition-opacity ${
                    isActive
                      ? channel.channelType === 'all'
                        ? 'text-white/80'
                        : 'opacity-70'
                      : 'text-slate-400'
                  }`}
                >
                  ({total > 99 ? '99+' : total})
                </span>
              )}
              {unread > 0 && (
                <span
                  className={`text-[9px] font-bold h-4 min-w-[16px] px-1 rounded-full flex items-center justify-center ${
                    isActive
                      ? channel.channelType === 'all'
                        ? 'bg-white text-[#075f55]'
                        : 'bg-red-500 text-white'
                      : 'bg-emerald-500 text-white'
                  }`}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Difuminado suave a la derecha */}
      {canScrollRight && (
        <div className="absolute right-3 top-0 bottom-0 w-6 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none z-5" />
      )}

      {/* Botón de desplazamiento hacia la derecha */}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollByAmount(140)}
          aria-label="Ver más canales"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full bg-white/95 border border-slate-200 text-slate-700 shadow-xs flex items-center justify-center hover:bg-slate-50 hover:scale-105 active:scale-95 transition-all focus:outline-none"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

export default ChannelFilterBar
